import { Box, Flex, styled } from "styled-system/jsx";
import { Button, Tag } from "@ttc3k/trekker";
import { ArrowLeft, ArrowRight } from "iconoir-react";
import { useIsTouchDevice, useScroll } from "shared/hooks";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { PostHogCustomEvent } from "shared/hooks/usePostHog";

type FilterCourselProps = {
  tags: { name: string; _id: string; count: number }[];
  selectedTag: string;
  onTagSelect: (id: string) => void;
};

const TagListContainer = styled("div", {
  base: {
    width: "100%",
    height: "100%",
    mx: "auto",
    overflowX: "scroll",
    scrollBehavior: "smooth",
    // Only display the scrollbar on mobile screens:
    scrollbarWidth: "none",
    display: "flex",
    alignItems: "center",
    gap: "100",
  },
  variants: {
    screenType: {
      nonTouch: {},
      touch: {
        scrollbarWidth: "auto",
      },
    },
  },
  defaultVariants: {
    screenType: "touch",
  },
});

export const FilterCarousel = ({
  tags,
  selectedTag,
  onTagSelect,
}: FilterCourselProps) => {
  const {
    scrollRef,
    atStart,
    atEnd,
    scrollLeftOrUp,
    scrollRightOrDown,
    isOverflowing,
  } = useScroll({
    mode: "horizontal",
    postHogEvent: {
      leftOrUp: PostHogCustomEvent.ScrollViewMoreTags,
      rightOrDown: PostHogCustomEvent.ScrollViewPreviousTags,
    },
  });

  const isTouchDevice = useIsTouchDevice();

  // Keep track of when the array changes to ensure each item has a unique key
  // This ensures the animation with animate fully out and then fully back in
  const [animationTime, setAnimationTime] = useState(new Date().getTime());
  useEffect(() => {
    setAnimationTime(new Date().getTime());
  }, [tags]);

  if (tags.length < 1) {
    return <Box h="48px" />;
  }

  const displayPrevArrow = !atStart && isOverflowing;
  const displayNextArrow = !atEnd && isOverflowing;

  return (
    <Box
      position="relative"
      overflowX="hidden"
      width="full"
      h="50px"
      borderTop="1px solid var(--wa-colors-ink-400)"
      borderBottom="1px solid var(--wa-colors-ink-400)"
    >
      <TagListContainer
        ref={scrollRef}
        screenType={isTouchDevice ? "touch" : "nonTouch"}
      >
        {tags.map((tag, index) => {
          const isSelectedTag = tag._id === selectedTag;
          return (
            <AnimatePresence
              mode="wait"
              key={`${tag._id}${tag.count}-${index}-${animationTime}`}
            >
              <motion.div
                key={`${tag._id}${tag.count}-${index}-${animationTime}`}
                layout={true}
                variants={{
                  hidden: {
                    opacity: 0,
                    width: 0,
                    transition: {
                      delay: (tags.length - index - 1) * 0.02,
                      duration: 0.1,
                    },
                  },
                  visible: {
                    opacity: 1,
                    width: "auto",
                    transition: {
                      delay: index * 0.04,
                      duration: 0.2,
                    },
                  },
                }}
                exit="hidden"
                animate="visible"
                initial="hidden"
              >
                <Tag
                  part={`filter__btn-tag${isSelectedTag ? "-selected" : ""}`}
                  visual={isSelectedTag ? "accent.blue" : "primary"}
                  mode={isSelectedTag ? "dark" : "light"}
                  onClick={() => onTagSelect?.(tag._id)}
                  whiteSpace="nowrap"
                  flexShrink="0"
                  tabIndex="0"
                >
                  {tag.name}
                  {tag.count > 0 && ` (${tag.count})`}
                </Tag>
              </motion.div>
            </AnimatePresence>
          );
        })}
      </TagListContainer>
      <Box position="absolute" width="full" top="0" zIndex={3}>
        {displayPrevArrow && (
          <Flex
            part={"filter__gradient-left"}
            position="absolute"
            left="0"
            top="-1px"
            height="48px"
            alignItems={"center"}
            py={"50"}
            width={isTouchDevice ? "24px" : "75px"}
            bg={
              "linear-gradient(90deg, var(--wa-colors-cream-100) 39.84%, rgba(239, 238, 235, 0.00) 100%)"
            }
          >
            {!isTouchDevice && (
              <Button
                part={"filter__btn-prev"}
                onClick={() => scrollLeftOrUp()}
                visual="primary"
                mode="light"
                borderRadius="full"
                Icon={ArrowLeft}
                outlineColor={"transparent"}
                w="32px"
                h="32px"
                boxShadow="0px 1px 4px 0px rgba(10, 10, 10, 0.20), 0px 1px 2px 0px rgba(10, 10, 10, 0.10)"
                ml="50"
              />
            )}
          </Flex>
        )}
        {displayNextArrow && (
          <Flex
            part={"filter__gradient-right"}
            position="absolute"
            right="0"
            top="-1px"
            height="48px"
            alignItems={"center"}
            py={"50"}
            width={isTouchDevice ? "24px" : "75px"}
            bg={
              "linear-gradient(270deg, var(--wa-colors-cream-100) 39.84%, rgba(239, 238, 235, 0.00) 100%)"
            }
            justifyContent={"flex-end"}
          >
            {!isTouchDevice && (
              <Button
                part={"filter__btn-next"}
                onClick={() => scrollRightOrDown()}
                visual="primary"
                mode="light"
                borderRadius="full"
                Icon={ArrowRight}
                disabled={atEnd}
                w="32px"
                h="32px"
                boxShadow="0px 1px 4px 0px rgba(10, 10, 10, 0.20), 0px 1px 2px 0px rgba(10, 10, 10, 0.10)"
                mr="50"
              />
            )}
          </Flex>
        )}
      </Box>
    </Box>
  );
};
